import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CommentIcon from '@mui/icons-material/Comment';
import ArticleFormModal from '../components/ArticleFormModal';
import ConfirmDialog from '../components/ConfirmDialog';
import ArticleCommentsModal from '../components/ArticleCommentsModal';
import ArticleFiltersConfigModal from '../components/ArticleFiltersConfigModal';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useListArticles, useDeleteArticle, useGetArticle, useListArticleComments, useDeleteArticleComment } from '../services/appService';
import AiDraftsModal from '../components/AiDraftsModal';
import { useAuth } from '../context/AuthContext';

// Helper to format date/time if needed
const formatDate = (dateInput) => {
    // Add check for null/undefined input
    console.log("[formatDate] Received input:", dateInput, "Type:", typeof dateInput);
    if (!dateInput) {
        console.log("[formatDate] Input is falsy, returning N/A.");
        return 'N/A'; 
    }
    try {
        // Ensure input is a Date object before formatting
        const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
        console.log("[formatDate] Converted to Date object:", date);
        // Check if the date is valid after conversion
        if (isNaN(date.getTime())) { 
            console.log("[formatDate] Date is invalid (isNaN), returning Invalid Date.");
            return 'Invalid Date'; 
        }
        const formatted = date.toLocaleString();
        console.log("[formatDate] Formatted string:", formatted);
        return formatted;
    } catch (e) {
        console.error("[formatDate] Error formatting date:", dateInput, e);
        return 'Invalid Date';
    }
}

function ArticlesPage() {
  const { appId } = useParams();
  const { isRedaktion, isOrgAdmin, getOrgIds } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null); // null for create, article object for edit
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState(null);
  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [selectedArticleForComments, setSelectedArticleForComments] = useState(null);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [isAiDraftsModalOpen, setIsAiDraftsModalOpen] = useState(false);

  const listArticlesHook = useListArticles();
  const deleteArticleHook = useDeleteArticle();
  const getArticleHook = useGetArticle();
  // const listCommentsHook = useListArticleComments();
  // const deleteCommentHook = useDeleteArticleComment();

  const fetchArticles = useCallback(async () => {
    if (!appId) return;
    setLoading(true);
    setError('');
    try {
      const data = await listArticlesHook(appId);
      setArticles(data || []);
    } catch (err) {
      console.error("Error fetching articles:", err);
      setError(err.message || 'Failed to load articles.');
    } finally {
      setLoading(false);
    }
  }, [appId, listArticlesHook]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const handleOpenCreateModal = () => {
    setEditingArticle(null); // Ensure it's in create mode
    setIsModalOpen(true);
  };

  const handleOpenEditModal = async (articleFromGrid) => {
    // articleFromGrid is the data directly from the DataGrid row.
    // It likely *lacks* the 'content' field because the list endpoint
    // (/api/apps/:app_id/articles) usually omits large fields for performance.

    // **** REQUIRED ****
    // To fix the missing content issue, you MUST fetch the full article details here.
    console.log("Opening edit modal for article summary:", articleFromGrid);
    setError('');
    setLoading(true); // Optional: show loading indicator

    try {
      // --- Replace this with an actual API call --- 
      // Example using a hypothetical hook:
      const fullArticle = await getArticleHook(appId, articleFromGrid.id);
      console.log("Fetched full article details:", fullArticle);
      setEditingArticle(fullArticle); // Use the COMPLETE data from the API
      
      // --- Previous Behavior (Removed) ---
      // console.warn("Fetching full article details is not yet implemented. Content may be missing.");
      // setEditingArticle(articleFromGrid); 
      // --- End Previous Behavior ---

      setIsModalOpen(true); // Open modal AFTER getting full data (or falling back)

    } catch (fetchErr) {
      console.error("Failed to fetch full article details:", fetchErr);
      setError(`Failed to load details for article ${articleFromGrid.id}. ${fetchErr.message}`);
      setEditingArticle(null); // Ensure modal doesn't open with bad state
      setIsModalOpen(false);
    } finally {
        setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingArticle(null);
  };

  const handleSaveArticle = () => {
    handleCloseModal();
    fetchArticles(); // Refresh list after save
  };

  const handleOpenConfirmDialog = (articleId) => {
    setArticleToDelete(articleId);
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setArticleToDelete(null);
    setConfirmDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!articleToDelete || !appId) return;
    setLoading(true); // Indicate loading state during delete
    try {
      await deleteArticleHook(appId, articleToDelete);
      fetchArticles(); // Refresh list
    } catch (err) {
        console.error("Error deleting article:", err);
        setError(err.message || 'Failed to delete article.');
        // Keep loading false or reset it here if needed, error alert will show
        setLoading(false);
    } finally {
      handleCloseConfirmDialog();
      // setLoading(false); // Already handled in try/catch for error case
    }
  };

  const handleOpenCommentsModal = (articleId) => {
      console.log("Opening comments modal for article ID:", articleId);
      setSelectedArticleForComments(articleId);
      setCommentsModalOpen(true);
  };

  const handleCloseCommentsModal = () => {
      setSelectedArticleForComments(null);
      setCommentsModalOpen(false);
  };

  const handleOpenFiltersModal = () => {
      setIsFiltersModalOpen(true);
  };

  const handleCloseFiltersModal = () => {
      setIsFiltersModalOpen(false);
  };

  const handleOpenAiDraftsModal = () => {
    setIsAiDraftsModalOpen(true);
  };

  const handleCloseAiDraftsModal = () => {
    setIsAiDraftsModalOpen(false);
  };

  const handleArticlesCreatedFromDrafts = () => {
    fetchArticles();
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 150 },
    { field: 'title', headerName: 'Title', width: 300, editable: false },
    { field: 'type', headerName: 'Type', width: 150 },
    { 
        field: 'is_published', 
        headerName: 'Published', 
        width: 100,
        type: 'boolean' 
    },
    {
        field: 'organization_name',
        headerName: 'Organization',
        width: 180,
        renderCell: (params) => {
            const orgName = params.row?.organizations?.name;
            return orgName || 'Redaktion';
        },
        sortable: false,
        filterable: false,
    },
    { 
        field: 'created_at', 
        headerName: 'Created At', 
        width: 180,
        type: 'dateTime',
        valueFormatter: (params) => {
             console.log("[CreatedAt ValueFormatter] params:", params);
             const rawDateString = params;
             console.log("[CreatedAt ValueFormatter] Raw date string from params:", rawDateString);
             return formatDate(rawDateString);
        } 
    },
    // Add columns for author_id, organization_id if needed
    // { field: 'author_id', headerName: 'Author ID', width: 250 },
    {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 100,
        cellClassName: 'actions',
        getActions: ({ id, row }) => {
            const orgIds = getOrgIds();
            const isOwn = !isOrgAdmin() || orgIds.includes(row.organization_id);
            const actions = [];

            if (isOwn) {
                actions.push(
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={() => handleOpenEditModal(row)}
                        color="inherit"
                    />
                );
            }
            actions.push(
                <GridActionsCellItem
                    icon={<CommentIcon />}
                    label="Comments"
                    onClick={() => handleOpenCommentsModal(id)}
                    color="inherit"
                />
            );
            if (isOwn) {
                actions.push(
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={() => handleOpenConfirmDialog(id)}
                        color="inherit"
                    />
                );
            }
            return actions;
        },
    },
];


  return (
    <Box sx={{ height: 'calc(100vh - 120px)', width: '100%', display: 'flex', flexDirection: 'column' }}> 
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
          <Typography variant="h4" gutterBottom>
            Manage Articles
          </Typography>
          <Box>
             {!isRedaktion() && !isOrgAdmin() && (
               <>
                 <Button
                      variant="outlined"
                      startIcon={<SettingsIcon />}
                      onClick={handleOpenFiltersModal}
                      sx={{ mr: 1 }}
                  >
                      Article Filters
                  </Button>
                  <Button
                      variant="outlined"
                      startIcon={<AutoFixHighIcon />}
                      onClick={handleOpenAiDraftsModal}
                      sx={{ mr: 1 }}
                  >
                      AI Filling
                  </Button>
               </>
             )}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateModal}
              >
                Create Article
              </Button>
          </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>{error}</Alert>}

      {loading ? (
         <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
             <CircularProgress />
         </Box>
      ) : (
          <Box sx={{ flexGrow: 1, width: '100%' }}> 
              <DataGrid
                  rows={articles}
                  columns={columns}
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{
                      pagination: {
                          paginationModel: { page: 0, pageSize: 10 }, 
                      },
                       sorting: {
                            sortModel: [{ field: 'created_at', sort: 'desc' }], 
                        },
                  }}
                  checkboxSelection={false}
                  disableRowSelectionOnClick
                  sx={{ 
                    border: 0, 
                    '& .MuiDataGrid-main': {
                         height: '100%', 
                    },
                     '& .MuiDataGrid-virtualScroller': {
                        height: 'calc(100% - 56px)',
                        overflowY: 'auto'
                    },
                  }} 
              />
          </Box>
      )}

      <ArticleFormModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveArticle}
        article={editingArticle}
        appId={appId}
      />

      <ArticleCommentsModal
        open={commentsModalOpen}
        onClose={handleCloseCommentsModal}
        appId={appId}
        articleId={selectedArticleForComments}
      />

      <ArticleFiltersConfigModal
        open={isFiltersModalOpen}
        onClose={handleCloseFiltersModal}
        appId={appId}
      />

      <AiDraftsModal
        open={isAiDraftsModalOpen}
        onClose={handleCloseAiDraftsModal}
        onArticlesCreated={handleArticlesCreatedFromDrafts}
        appId={appId}
      />

      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        onConfirm={handleDeleteConfirm}
        title="Delete Article?"
        message={`Are you sure you want to delete article ID: ${articleToDelete}? This action cannot be undone.`}
      />

    </Box>
  );
}

export default ArticlesPage; 